import { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/hooks";

import { useTranslation } from "react-i18next";
import { useMediaQuery } from "context/media/MediaContext";

import { Col, ElementWrapper, Row } from "components/BaseComponents/Flex";
import { Button, Spinner, Typography } from "components";
import { DIGIPOSTHEME } from "theme/digiPosTheme";

import { TokenInclude } from "helpers/GetToken";

import {
  StyledTitleWrapper,
  StyledShortcuts,
  StyledShortcutsCircleWrapper,
  StyledShortcutsIconCircle,
  StyledFaqItem,
} from "./Faq.styles";
import Image, { ICONS } from "components/BaseComponents/Image";
import { FaqListAsync } from "services/employeeProfile.service";
import { setServiceError } from "redux/slices/ServiceInlineError";
import {
  IFAQVideoListItem,
  IFaqListResponse,
} from "models/Employee/employeeProfile.model";
import Alert from "components/BaseComponents/Alert/Alert";
import SupportModal from "../ProfileInfo/SupportModal";
import { setMenuClick } from "redux/slices/StepSlice";

const FaqList: FC = () => {
  const { t } = useTranslation();
  const { isMobile } = useMediaQuery();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const token = TokenInclude();

  const { userOtp } = useAppSelector((state) => state.otp);

  const [loading, setLoading] = useState<boolean>(false);
  const [faqListData, setFaqListData] = useState<IFaqListResponse>();

  const [isAddSuccess, setIsAddSuccess] = useState<boolean>(false);
  const [isAddError, setIsAddError] = useState<boolean>(false);
  const [txt, setTxt] = useState<string>("");

  const [openModal, setOpenModal] = useState<boolean>(false);

  const openSupportModal = (e: MouseEvent) => {
    e.stopPropagation();
    setOpenModal((prev) => !prev);
  };

  const goWorkingConditions = () => {
    dispatch(setMenuClick(1));
    navigate("/working-conditions", {
      state: {
        isFaq: true,
      },
    });
  };

  const goEmployeePanel = () => {
    dispatch(setMenuClick(2));
    navigate("/employee-panel");
  };

  useEffect(() => {
    async function runFaqList() {
      setLoading(true);
      const retVal = await FaqListAsync(token);
      if (retVal?.DialogBoxes?.length < 1) {
        setFaqListData(retVal);
        setLoading(false);
      } else {
        const errMessage: any = retVal;
        setLoading(false);
        dispatch(setServiceError(errMessage));
        return;
      }
    }
    runFaqList();
  }, []);

  return (
    <ElementWrapper
      width='872px'
      direction='column'
      justifyContent='flex-start'
      alignItems='flex-start'
      margin='10px auto'
      padding={`${isMobile ? "0 5px !important" : ""}`}
    >
      <Spinner show={loading} />
      {isAddError && (
        <Alert
          alertType='Error'
          duration={9000}
          isVisible={isAddError}
          message={txt}
          setIsAddSuccess={setIsAddSuccess}
          setIsAddError={setIsAddError}
        />
      )}
      <StyledTitleWrapper>
        <Typography width='' size='16px' color={DIGIPOSTHEME.colors.white}>
          {t("question_page_title")}
        </Typography>
      </StyledTitleWrapper>

      <Row
        width='100%'
        backgroundColor={DIGIPOSTHEME.colors.transparent}
        justifyContent='flex-start'
        noMargin
      >
        <Typography
          width=''
          margin='10px 0'
          size='17px'
          color={DIGIPOSTHEME.colors.secondaryGraySecondary}
          transform='uppercase'
        >
          {t("Shortcuts")}
        </Typography>
      </Row>
      <StyledShortcuts>
        <StyledShortcutsCircleWrapper>
          <StyledShortcutsIconCircle>
            <Image
              alt='Menu Icon'
              height='20px'
              srcSet={ICONS.SUPPORT}
              width='20px'
              onClick={(e: any) => openSupportModal(e)}
            />
          </StyledShortcutsIconCircle>
          <Typography width='' size='14px'>
            {t("support")}
          </Typography>
        </StyledShortcutsCircleWrapper>

        {(userOtp?.UserLevel === "1" || userOtp?.UserLevel === "3") && (
          <>
            <StyledShortcutsCircleWrapper>
              <StyledShortcutsIconCircle>
                <Image
                  alt='Menu Icon'
                  height='50px'
                  srcSet={ICONS.EMPLOYEES}
                  width='50px'
                  onClick={() => goEmployeePanel()}
                />
              </StyledShortcutsIconCircle>
              <Typography width='' size='14px'>
                {t("agent_management")}
              </Typography>
            </StyledShortcutsCircleWrapper>

            <StyledShortcutsCircleWrapper>
              <StyledShortcutsIconCircle>
                <Image
                  alt='Menu Icon'
                  height='20px'
                  srcSet={ICONS.TXT}
                  width='20px'
                  onClick={() => goWorkingConditions()}
                />
              </StyledShortcutsIconCircle>
              <Typography width='' size='14px'>
                {t("Working_conditions")}
              </Typography>
            </StyledShortcutsCircleWrapper>
          </>
        )}
      </StyledShortcuts>

      <Row
        padding='25px 0'
        backgroundColor={DIGIPOSTHEME.colors.transparent}
        width='100%'
        noMargin
      >
        <Typography
          size='17px'
          color={DIGIPOSTHEME.colors.secondaryGraySecondary}
          transform='uppercase'
        >
          {t("question_page_title")}
        </Typography>

        {faqListData?.FAQInfo.FAQVideoList?.map(
          (faq: IFAQVideoListItem, index: number) => (
            <StyledFaqItem
              key={index}
              onClick={() => navigate(`/faq-details/${faq.FAQKey}`)}
            >
              <Typography
                margin='10px 0 0 0'
                size='15px'
                color={DIGIPOSTHEME.colors.secondaryBlack}
                weight='600'
                transform='capitalize'
                hoverColor={DIGIPOSTHEME.colors.headerRed}
              >
                {faq.FAQTitle}
              </Typography>
              <Image
                alt='PDF Icon'
                height='25px'
                srcSet={ICONS.REDRIGHT}
                width='25px'
              />
            </StyledFaqItem>
          )
        )}
      </Row>

      <Row
        backgroundColor={DIGIPOSTHEME.colors.secondaryOffWhite}
        justifyContent='space-between'
        isResponsive
        noMargin
      >
        <Col>
          <Button
            type='button'
            width={isMobile ? "100%" : "100px"}
            text={`← ${t("back")}`}
            onClick={() => navigate(-1)}
            color='light'
          />
        </Col>
      </Row>

      {openModal && (
        <SupportModal
          rejectButtonText={t("modal_error_button_title").toString()}
          message={t("support_warning").toString()}
          setIsOpen={setOpenModal}
          title={t("information").toString()}
        />
      )}
    </ElementWrapper>
  );
};

export default FaqList;
